import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail, BRAND } from "@/lib/api";
import { LayoutDashboard, LogOut, Eye, EyeOff } from "lucide-react";

const Card = ({ children }) => (
  <div className="min-h-screen flex items-center justify-center bg-[#1A1A1A] px-5">
    <div className="w-full max-w-md bg-white rounded-2xl p-10 shadow-xl">
      <div className="text-center mb-8">
        <div className="h-16 w-16 gh-octagon bg-black mx-auto flex items-center justify-center overflow-hidden ring-1 ring-[#D9CBA0]/50 mb-4">
          <img src={BRAND.logo} alt="Hope & Favor" className="h-full w-full object-contain" />
        </div>
        <h1 className="font-heading text-3xl text-[#3A3632]">Hope &amp; Favor</h1>
        <p className="text-sm text-[#8A8175] mt-1">Admin dashboard</p>
      </div>
      {children}
    </div>
  </div>
);

export default function Login() {
  const { login, logout, user, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [showPw, setShowPw] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setBusy(true);
    try { await login(email, password); nav("/admin"); }
    catch (err) { setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed."); }
    finally { setBusy(false); }
  };

  if (loading) return <Card><p className="text-center text-[#8A8175]">Checking your session…</p></Card>;

  if (user) return (
    <Card>
      <div className="text-center" data-testid="already-signed-in">
        <p className="text-[#3A3632] mb-1">You're signed in as</p>
        <p className="font-medium text-[#8E9A86] mb-6 break-all">{user.email}</p>
        <button onClick={() => nav("/admin")} data-testid="go-to-dashboard" className="w-full bg-[#8E9A86] text-white rounded-full py-3 font-medium hover:bg-[#7A8772] transition-colors inline-flex items-center justify-center gap-2 mb-3">
          <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
        </button>
        <button onClick={async () => { await logout(); }} data-testid="login-signout" className="w-full border border-[#D1CCC5] text-[#6B655F] rounded-full py-3 font-medium hover:bg-[#FAF7F2] transition-colors inline-flex items-center justify-center gap-2">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
        <Link to="/" className="block text-sm text-[#8A8175] mt-5 hover:text-[#8E9A86]">← Back to site</Link>
      </div>
    </Card>
  );

  return (
    <Card>
      <form onSubmit={submit} data-testid="login-form" className="space-y-4">
        <input type="email" name="email" autoComplete="username" required placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
        <div className="relative">
          <input type={showPw ? "text" : "password"} name="password" autoComplete="current-password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" className="w-full border border-[#D1CCC5] rounded-lg px-4 py-3 pr-12 outline-none focus:border-[#8E9A86] focus:ring-2 focus:ring-[#8E9A86]/30" />
          <button type="button" onClick={() => setShowPw((s) => !s)} data-testid="toggle-password-visibility" aria-label={showPw ? "Hide password" : "Show password"} tabIndex={-1}
            className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#8A8175] hover:text-[#8E9A86]">
            {showPw ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
        {error && <p className="text-sm text-red-600" data-testid="login-error">{error}</p>}
        <button type="submit" disabled={busy} data-testid="login-submit" className="w-full bg-[#8E9A86] text-white rounded-full py-3 font-medium hover:bg-[#7A8772] transition-colors disabled:opacity-60">{busy ? "Signing in…" : "Sign In"}</button>
      </form>
      <Link to="/forgot-password" data-testid="forgot-password-link" className="block text-center text-sm text-[#8E9A86] mt-4 hover:underline">Forgot your password?</Link>
      <Link to="/" className="block text-center text-sm text-[#8A8175] mt-3 hover:text-[#8E9A86]">← Back to site</Link>
    </Card>
  );
}
